import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  MessageSquare, Mail, User, Calendar, CheckCircle, Circle,
  Loader2, Inbox, ArrowLeft, UserPlus, Save, Archive, Phone,
  ClipboardList, ExternalLink, X
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  new: { label: 'New', className: 'bg-[#D72B2B]/10 text-[#D72B2B]' },
  contacted: { label: 'Contacted', className: 'bg-[#1A2E6E]/10 text-[#1A2E6E]' },
  qualified: { label: 'Qualified', className: 'bg-[#F5A623]/10 text-[#C97D0A]' },
  converted: { label: 'Converted', className: 'bg-[#4CAF50]/10 text-[#4CAF50]' },
  archived: { label: 'Archived', className: 'bg-[#1A2E6E]/8 text-[#1A2E6E]/55' },
};

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'new', label: 'New' },
  { id: 'contacted', label: 'Contacted' },
  { id: 'qualified', label: 'Qualified' },
  { id: 'converted', label: 'Converted' },
  { id: 'archived', label: 'Archived' },
  { id: 'unread', label: 'Unread' },
];

const WORKFLOW_OPTIONS = ['new', 'contacted', 'qualified', 'archived'];

const getStatus = (contact) => contact?.status || 'new';

const getErrorMessage = (err, fallback) =>
  err?.response?.data?.detail || err?.response?.data?.message || fallback;

export const AdminMessages = () => {
  const { isStrictAdmin } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [filter, setFilter] = useState('all');
  const [notesDraft, setNotesDraft] = useState('');
  const [savingContact, setSavingContact] = useState(false);
  const [conversionOpen, setConversionOpen] = useState(false);
  const [conversionForm, setConversionForm] = useState({
    password: '',
    approve_student: false,
    internal_notes: '',
  });
  const [converting, setConverting] = useState(false);
  const [conversionResult, setConversionResult] = useState(null);

  useEffect(() => {
    const fetchContacts = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/api/admin/contacts?page_size=200`);
        setContacts(response.data.data ?? response.data);
      } catch (err) {
        console.error('Failed to fetch contacts:', err);
        toast.error(getErrorMessage(err, 'Failed to load contacts'));
      } finally {
        setLoading(false);
      }
    };

    fetchContacts();
  }, []);

  const replaceContact = (updatedContact) => {
    setContacts(prev => prev.map(contact => (
      contact.id === updatedContact.id ? updatedContact : contact
    )));
    setSelectedMessage(prev => (
      prev?.id === updatedContact.id ? updatedContact : prev
    ));
  };

  const updateContact = async (contactId, payload, successMessage) => {
    try {
      setSavingContact(true);
      const response = await axios.put(`${API_URL}/api/admin/contacts/${contactId}`, payload);
      replaceContact(response.data);
      if (successMessage) toast.success(successMessage);
      return response.data;
    } catch (err) {
      console.error('Failed to update contact:', err);
      toast.error(getErrorMessage(err, 'Failed to update contact'));
      return null;
    } finally {
      setSavingContact(false);
    }
  };

  const markAsRead = async (contactId) => {
    try {
      const response = await axios.put(`${API_URL}/api/admin/contacts/${contactId}/read`);
      if (response.data.contact) replaceContact(response.data.contact);
    } catch (err) {
      console.error('Failed to mark contact as read:', err);
    }
  };

  const handleSelectMessage = (message) => {
    setSelectedMessage(message);
    setNotesDraft(message.admin_notes || '');
    setConversionResult(null);
    if (!message.read) markAsRead(message.id);
  };

  const handleReply = async () => {
    if (!selectedMessage) return;
    if (getStatus(selectedMessage) === 'new') {
      await updateContact(selectedMessage.id, { status: 'contacted', read: true });
    }
    window.location.href = `mailto:${selectedMessage.email}?subject=Re: Contact from Frankly Talking English`;
  };

  const handleSaveNotes = () => {
    if (!selectedMessage) return;
    updateContact(
      selectedMessage.id,
      { admin_notes: notesDraft },
      'Lead notes saved'
    );
  };

  const handleConvert = async () => {
    if (!selectedMessage) return;
    try {
      setConverting(true);
      const payload = {
        approve_student: conversionForm.approve_student,
      };
      if (conversionForm.password.trim()) payload.password = conversionForm.password.trim();
      if (conversionForm.internal_notes.trim()) payload.internal_notes = conversionForm.internal_notes.trim();

      const response = await axios.post(
        `${API_URL}/api/admin/contacts/${selectedMessage.id}/convert`,
        payload
      );
      replaceContact(response.data.contact);
      setConversionResult(response.data);
      setConversionOpen(false);
      setConversionForm({ password: '', approve_student: false, internal_notes: '' });
      toast.success(response.data.existing_student ? 'Lead linked to existing student' : 'Lead converted to student');
    } catch (err) {
      console.error('Failed to convert contact:', err);
      toast.error(getErrorMessage(err, 'Failed to convert lead'));
    } finally {
      setConverting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    }
    if (days === 1) return 'Yesterday';
    if (days < 7) return date.toLocaleDateString('en-GB', { weekday: 'short' });
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  const filteredContacts = contacts.filter(contact => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !contact.read;
    return getStatus(contact) === filter;
  });

  const stats = {
    total: contacts.length,
    unread: contacts.filter(contact => !contact.read).length,
    open: contacts.filter(contact => !['converted', 'archived'].includes(getStatus(contact))).length,
    converted: contacts.filter(contact => getStatus(contact) === 'converted').length,
  };

  const detailItems = selectedMessage ? [
    { label: 'Phone / WhatsApp', value: selectedMessage.phone, icon: Phone },
    {
      label: 'Inquiry for',
      value:
        selectedMessage.inquiry_for === 'another-person'
          ? 'Another person'
          : selectedMessage.inquiry_for === 'myself'
            ? 'Myself'
            : selectedMessage.inquiry_for === 'group'
              ? 'A group of people'
              : selectedMessage.inquiry_for === 'company'
                ? 'A company / team'
                : null,
      icon: ClipboardList,
    },
    { label: 'Student age', value: selectedMessage.student_age },
    { label: 'Group size', value: selectedMessage.group_size },
    { label: 'Age range', value: selectedMessage.age_range },
    { label: 'Company name', value: selectedMessage.company_name },
    { label: 'Team size', value: selectedMessage.team_size },
    { label: 'Native language', value: selectedMessage.native_language },
    { label: 'Country', value: selectedMessage.country },
  ].filter(item => item.value !== null && item.value !== undefined && item.value !== '') : [];

  const currentStatus = getStatus(selectedMessage);
  const statusMeta = STATUS_META[currentStatus] || STATUS_META.new;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#D72B2B] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-messages-page">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#1A2E6E] font-['Sora']">
            Contacts
          </h1>
          <p className="text-[#1A2E6E]/60 mt-1">
            Website leads, follow-up notes and student conversion.
          </p>
        </div>
        <Link
          to="/admin/leads"
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-[#E8EAF0] text-[#1A2E6E] rounded-xl hover:bg-[#1A2E6E]/5 transition-colors text-sm font-medium"
        >
          <ExternalLink size={16} />
          Lead Overview
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-[#E8EAF0] p-4">
          <p className="text-2xl font-bold text-[#1A2E6E]">{stats.total}</p>
          <p className="text-sm text-[#1A2E6E]/60">Total contacts</p>
        </div>
        <div className="bg-[#D72B2B]/5 rounded-xl border border-[#D72B2B]/20 p-4">
          <p className="text-2xl font-bold text-[#D72B2B]">{stats.unread}</p>
          <p className="text-sm text-[#1A2E6E]/60">Unread</p>
        </div>
        <div className="bg-[#F5A623]/5 rounded-xl border border-[#F5A623]/25 p-4">
          <p className="text-2xl font-bold text-[#C97D0A]">{stats.open}</p>
          <p className="text-sm text-[#1A2E6E]/60">Open leads</p>
        </div>
        <div className="bg-[#4CAF50]/5 rounded-xl border border-[#4CAF50]/20 p-4">
          <p className="text-2xl font-bold text-[#4CAF50]">{stats.converted}</p>
          <p className="text-sm text-[#1A2E6E]/60">Converted</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 bg-white rounded-xl p-1.5 border border-[#E8EAF0] w-fit max-w-full">
        {FILTERS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setFilter(tab.id)}
            className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-all ${
              filter === tab.id
                ? 'bg-[#1A2E6E] text-white'
                : 'text-[#1A2E6E]/70 hover:text-[#1A2E6E] hover:bg-[#1A2E6E]/5'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-[#E8EAF0] overflow-hidden min-h-[560px]">
        <div className="flex flex-col lg:flex-row min-h-[560px]">
          <div className={`
            w-full lg:w-[25rem] border-r border-[#E8EAF0] overflow-y-auto max-h-[560px]
            ${selectedMessage ? 'hidden lg:block' : 'block'}
          `}>
            {filteredContacts.length > 0 ? (
              <div className="divide-y divide-[#E8EAF0]">
                {filteredContacts.map((message, index) => {
                  const messageStatus = getStatus(message);
                  const meta = STATUS_META[messageStatus] || STATUS_META.new;
                  return (
                    <motion.button
                      key={message.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.02 }}
                      onClick={() => handleSelectMessage(message)}
                      className={`
                        w-full p-4 text-left hover:bg-[#FAFAF7] transition-colors
                        ${selectedMessage?.id === message.id ? 'bg-[#FAFAF7]' : ''}
                        ${!message.read ? 'bg-[#D72B2B]/5' : ''}
                      `}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`
                          w-10 h-10 rounded-full flex items-center justify-center shrink-0
                          ${!message.read ? 'bg-[#D72B2B]/10' : 'bg-[#1A2E6E]/10'}
                        `}>
                          <User className={`w-5 h-5 ${!message.read ? 'text-[#D72B2B]' : 'text-[#1A2E6E]'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <span className={`font-medium truncate ${!message.read ? 'text-[#1A2E6E]' : 'text-[#1A2E6E]/80'}`}>
                              {message.name}
                            </span>
                            <span className="text-xs text-[#1A2E6E]/50 shrink-0">
                              {formatDate(message.created_at)}
                            </span>
                          </div>
                          <p className="text-sm text-[#1A2E6E]/60 truncate mb-1">
                            {message.email}
                          </p>
                          <p className="text-sm text-[#1A2E6E]/70 line-clamp-2">
                            {message.message}
                          </p>
                          <div className="flex flex-wrap items-center gap-2 mt-2">
                            <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${meta.className}`}>
                              {!message.read && <Circle size={7} className="fill-current" />}
                              {meta.label}
                            </span>
                            {!message.read && (
                              <span className="text-xs text-[#D72B2B] font-medium">Unread</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            ) : (
              <div className="h-full min-h-[420px] flex flex-col items-center justify-center p-8">
                <Inbox className="w-16 h-16 text-[#1A2E6E]/20 mb-4" />
                <p className="text-[#1A2E6E] font-semibold text-center">
                  No contacts in this view
                </p>
                <p className="text-[#1A2E6E]/55 text-sm text-center mt-1 max-w-xs">
                  New website contact requests will appear here with their follow-up status.
                </p>
              </div>
            )}
          </div>

          <div className={`
            flex-1 flex flex-col min-w-0
            ${selectedMessage ? 'block' : 'hidden lg:flex'}
          `}>
            {selectedMessage ? (
              <>
                <button
                  onClick={() => setSelectedMessage(null)}
                  className="lg:hidden p-4 border-b border-[#E8EAF0] text-sm text-[#1A2E6E] hover:bg-[#FAFAF7] flex items-center gap-2"
                >
                  <ArrowLeft size={16} />
                  Back to contacts
                </button>

                <div className="p-6 border-b border-[#E8EAF0]">
                  <div className="flex flex-col xl:flex-row xl:items-start gap-4">
                    <div className="flex items-start gap-4 flex-1 min-w-0">
                      <div className="w-14 h-14 rounded-full bg-[#1A2E6E]/10 flex items-center justify-center shrink-0">
                        <User className="w-7 h-7 text-[#1A2E6E]" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <h2 className="text-xl font-semibold text-[#1A2E6E]">
                            {selectedMessage.name}
                          </h2>
                          <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${statusMeta.className}`}>
                            {statusMeta.label}
                          </span>
                          {selectedMessage.read && (
                            <span className="inline-flex items-center gap-1 text-xs text-[#4CAF50] bg-[#4CAF50]/10 px-2 py-1 rounded-full font-semibold">
                              <CheckCircle size={12} />
                              Read
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-4 text-sm text-[#1A2E6E]/60">
                          <a
                            href={`mailto:${selectedMessage.email}`}
                            className="flex items-center gap-1 hover:text-[#D72B2B] transition-colors"
                          >
                            <Mail size={14} />
                            {selectedMessage.email}
                          </a>
                          <span className="flex items-center gap-1">
                            <Calendar size={14} />
                            {new Date(selectedMessage.created_at).toLocaleString('en-GB')}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-2">
                      <select
                        value={currentStatus}
                        disabled={savingContact || currentStatus === 'converted'}
                        onChange={(event) => updateContact(selectedMessage.id, { status: event.target.value }, 'Lead status updated')}
                        className="px-3 py-2 rounded-xl border border-[#E8EAF0] text-sm text-[#1A2E6E] bg-white focus:outline-none focus:ring-2 focus:ring-[#D72B2B]/30"
                      >
                        {currentStatus === 'converted' && <option value="converted">Converted</option>}
                        {WORKFLOW_OPTIONS.map(status => (
                          <option key={status} value={status}>{STATUS_META[status].label}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => updateContact(selectedMessage.id, { status: 'archived' }, 'Lead archived')}
                        disabled={savingContact || currentStatus === 'converted' || currentStatus === 'archived'}
                        className="inline-flex items-center justify-center gap-2 px-3 py-2 border border-[#E8EAF0] text-[#1A2E6E] rounded-xl hover:bg-[#1A2E6E]/5 transition-colors text-sm font-medium disabled:opacity-50"
                      >
                        <Archive size={16} />
                        Archive
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex-1 p-6 overflow-y-auto space-y-6">
                  {conversionResult?.contact?.id === selectedMessage.id && (
                    <div className="rounded-xl border border-[#4CAF50]/25 bg-[#4CAF50]/5 p-4">
                      <p className="text-sm font-semibold text-[#1A2E6E]">
                        Student account ready
                      </p>
                      <p className="text-sm text-[#1A2E6E]/65 mt-1">
                        Continue in Students to approve, assign a plan, and schedule classes.
                      </p>
                      {conversionResult.temporary_password && (
                        <div className="mt-3 rounded-lg bg-white border border-[#E8EAF0] p-3">
                          <p className="text-xs uppercase tracking-wide text-[#1A2E6E]/45 font-semibold">
                            Temporary password
                          </p>
                          <p className="text-sm font-semibold text-[#1A2E6E] mt-1">
                            {conversionResult.temporary_password}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {detailItems.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                      {detailItems.map((item) => {
                        const Icon = item.icon;
                        return (
                          <div
                            key={item.label}
                            className="rounded-xl border border-[#E8EAF0] bg-[#FAFAF7] p-4"
                          >
                            <p className="text-xs font-semibold uppercase tracking-wide text-[#1A2E6E]/45 mb-1 flex items-center gap-1.5">
                              {Icon && <Icon size={13} />}
                              {item.label}
                            </p>
                            <p className="text-sm text-[#1A2E6E]/80">
                              {item.value}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div className="rounded-xl border border-[#E8EAF0] p-5">
                    <p className="text-xs font-semibold uppercase tracking-wide text-[#1A2E6E]/45 mb-3">
                      Message
                    </p>
                    <p className="text-[#1A2E6E]/80 whitespace-pre-wrap leading-relaxed text-base">
                      {selectedMessage.message}
                    </p>
                  </div>

                  <div className="rounded-xl border border-[#E8EAF0] p-5">
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <div>
                        <p className="text-sm font-semibold text-[#1A2E6E]">Internal notes</p>
                        <p className="text-xs text-[#1A2E6E]/50">Visible only in the Admin panel.</p>
                      </div>
                      <button
                        onClick={handleSaveNotes}
                        disabled={savingContact}
                        className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-[#1A2E6E] text-white rounded-xl hover:bg-[#14265C] transition-colors text-sm font-medium disabled:opacity-60"
                      >
                        {savingContact ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                        Save
                      </button>
                    </div>
                    <textarea
                      value={notesDraft}
                      onChange={(event) => setNotesDraft(event.target.value)}
                      rows={4}
                      className="w-full px-4 py-3 rounded-xl border border-[#E8EAF0] text-sm text-[#1A2E6E] focus:outline-none focus:ring-2 focus:ring-[#D72B2B]/30"
                      placeholder="Add follow-up notes, preferred plan, schedule constraints, or conversion context."
                    />
                  </div>
                </div>

                <div className="p-4 border-t border-[#E8EAF0] bg-[#FAFAF7] flex flex-wrap gap-2">
                  <button
                    onClick={handleReply}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#D72B2B] text-white rounded-xl hover:bg-[#B71C1C] transition-colors font-medium"
                  >
                    <Mail size={18} />
                    Reply via Email
                  </button>
                  {isStrictAdmin && (
                    <button
                      onClick={() => {
                        setConversionForm(prev => ({
                          ...prev,
                          internal_notes: notesDraft || selectedMessage.message || '',
                        }));
                        setConversionOpen(true);
                      }}
                      disabled={currentStatus === 'converted'}
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1A2E6E] text-white rounded-xl hover:bg-[#14265C] transition-colors font-medium disabled:opacity-50"
                    >
                      <UserPlus size={18} />
                      Convert to Student
                    </button>
                  )}
                  {selectedMessage.linked_student_id && (
                    <Link
                      to={`/admin/students?search=${encodeURIComponent(selectedMessage.email)}`}
                      className="inline-flex items-center gap-2 px-5 py-2.5 border border-[#E8EAF0] text-[#1A2E6E] rounded-xl hover:bg-white transition-colors font-medium"
                    >
                      <User size={18} />
                      View Student
                    </Link>
                  )}
                </div>
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center p-8">
                <MessageSquare className="w-16 h-16 text-[#1A2E6E]/20 mb-4" />
                <p className="text-[#1A2E6E] font-semibold text-center">
                  Select a contact to manage follow-up
                </p>
                <p className="text-[#1A2E6E]/55 text-sm text-center mt-1 max-w-sm">
                  From here you can reply, qualify, archive, convert to student, then continue with plans and scheduling.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {conversionOpen && selectedMessage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => !converting && setConversionOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-4 mb-5">
                <div>
                  <h3 className="text-xl font-bold text-[#1A2E6E] font-['Sora']">
                    Convert lead to student
                  </h3>
                  <p className="text-sm text-[#1A2E6E]/60 mt-1">
                    Create a student account for {selectedMessage.name}.
                  </p>
                </div>
                <button
                  onClick={() => setConversionOpen(false)}
                  disabled={converting}
                  className="p-2 text-[#1A2E6E]/50 hover:text-[#1A2E6E] hover:bg-[#1A2E6E]/5 rounded-lg"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="space-y-4">
                <div className="rounded-xl border border-[#E8EAF0] bg-[#FAFAF7] p-4">
                  <p className="text-sm font-semibold text-[#1A2E6E]">{selectedMessage.name}</p>
                  <p className="text-sm text-[#1A2E6E]/60">{selectedMessage.email}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                    Temporary password
                  </label>
                  <input
                    type="text"
                    value={conversionForm.password}
                    onChange={(event) => setConversionForm(prev => ({ ...prev, password: event.target.value }))}
                    placeholder="Leave empty to generate automatically"
                    className="w-full px-4 py-3 rounded-xl border border-[#E8EAF0] focus:outline-none focus:ring-2 focus:ring-[#D72B2B]/30 text-[#1A2E6E]"
                  />
                  <p className="text-xs text-[#1A2E6E]/50 mt-1">
                    The generated password will be shown once after conversion.
                  </p>
                </div>

                <label className="flex items-start gap-3 rounded-xl border border-[#E8EAF0] p-4 cursor-pointer hover:bg-[#FAFAF7]">
                  <input
                    type="checkbox"
                    checked={conversionForm.approve_student}
                    onChange={(event) => setConversionForm(prev => ({ ...prev, approve_student: event.target.checked }))}
                    className="mt-1"
                  />
                  <span>
                    <span className="block text-sm font-semibold text-[#1A2E6E]">
                      Approve student immediately
                    </span>
                    <span className="block text-xs text-[#1A2E6E]/55 mt-0.5">
                      Leave unchecked if the student still needs admin review before accessing paid classes.
                    </span>
                  </span>
                </label>

                <div>
                  <label className="block text-sm font-medium text-[#1A2E6E] mb-2">
                    Student internal notes
                  </label>
                  <textarea
                    rows={4}
                    value={conversionForm.internal_notes}
                    onChange={(event) => setConversionForm(prev => ({ ...prev, internal_notes: event.target.value }))}
                    className="w-full px-4 py-3 rounded-xl border border-[#E8EAF0] focus:outline-none focus:ring-2 focus:ring-[#D72B2B]/30 text-[#1A2E6E]"
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6">
                <button
                  onClick={() => setConversionOpen(false)}
                  disabled={converting}
                  className="px-5 py-2.5 border border-[#E8EAF0] text-[#1A2E6E] rounded-xl hover:bg-[#1A2E6E]/5 transition-colors font-medium disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConvert}
                  disabled={converting}
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-[#D72B2B] text-white rounded-xl hover:bg-[#B71C1C] transition-colors font-medium disabled:opacity-60"
                >
                  {converting ? <Loader2 size={18} className="animate-spin" /> : <UserPlus size={18} />}
                  Convert
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminMessages;
